import { useEffect, useState } from 'react';
import { useStore } from '../state/store';
import { ws } from '../ws/client';

type DocUrlResult = { url: string; title: string; summary: string };

/** Embed URL that hides the browser PDF sidebar (page thumbnails / outline). */
export function pdfViewerSrc(url: string): string {
  const hashIdx = url.indexOf('#');
  const base = hashIdx === -1 ? url : url.slice(0, hashIdx);
  const existing = hashIdx === -1 ? '' : url.slice(hashIdx + 1);
  const params = new URLSearchParams(existing);
  params.set('navpanes', '0');
  return `${base}#${params.toString()}`;
}

export function DocumentViewer(): React.ReactNode {
  const selectedId = useStore((s) => s.selectedDocumentId);
  const documents = useStore((s) => s.caseDocuments);
  const setSelected = useStore((s) => s.setSelectedDocumentId);
  const meta = documents.find((d) => d.id === selectedId) ?? null;
  const [payload, setPayload] = useState<DocUrlResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!selectedId) {
      setPayload(null);
      setError(null);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    ws.rpc<DocUrlResult>('case.documentUrl', { documentId: selectedId })
      .then((r) => {
        if (!cancelled) setPayload(r);
      })
      .catch((err: unknown) => {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Failed to load document');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [selectedId]);

  if (!selectedId || !meta) return null;

  return (
    <div className="doc-viewer">
      <div className="doc-viewer-head">
        <div className="doc-viewer-titles">
          <h3>{meta.title}</h3>
          {meta.summary ? <p className="dim">{meta.summary}</p> : null}
        </div>
        <button type="button" className="small" onClick={() => setSelected(null)}>
          Close
        </button>
      </div>
      {loading && <p className="dim">Loading PDF…</p>}
      {error && <p className="doc-viewer-error">{error}</p>}
      {payload?.url && (
        <iframe className="doc-viewer-frame" title={payload.title} src={pdfViewerSrc(payload.url)} />
      )}
    </div>
  );
}
